import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import {
  CompanyStatus, ICompany, ICompanyAddress, ICompanyCreateRequest, ICompanyIdentityRequest, ICompanyList, ICompanyListItem, ISchedule,
} from '../models/company.model';
import { ICompanySocialNetwork, ICompanySocialNetworkRequest } from '../models/catalog.model';
import { ServerResponse } from '../models/server-response.model';
import { canUseWeb } from '../utils/company-role.util';

/** Toast al staff que abre un negocio por URL: su rol no usa la web. */
export const STAFF_WEB_DENIED = 'Tu rol Staff solo tiene acceso a la app CradleRewards Console';

/** title/tooltip de las acciones de escritura deshabilitadas porque el negocio está inactivo (status 0). */
export const INACTIVE_TITLE = 'Negocio inactivo';

export interface ICompanyQuery {
  q?: string;
  idCategory?: string;
  orderBy?: 'most_active' | 'name' | 'recent';
  page?: number;
  limit?: number;
  /** Solo GET /sudo/companies: 1 activos · 0 inactivos (sin él, todos). */
  status?: CompanyStatus;
}

/** Negocios (COMPANIES) y sus subrecursos: ubicación, horario y redes. */
@Injectable({ providedIn: 'root' })
export class CompanyService {

  private api = inject(ApiService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private router = inject(Router);

  /** Negocio abierto en el BusinessShell: SIEMPRE el detalle completo de GET /companies/{idCompany} (null mientras llega). */
  readonly current = signal<ICompany | null>(null);
  /**
   * Datos parciales del negocio que se está abriendo (item de la lista de Negocios o del selector del sidebar).
   * Pintan el encabezado y el menú al instante mientras llega el detalle completo a `current`.
   */
  readonly preview = signal<ICompanyListItem | null>(null);
  /** idCompany cuyo detalle se está pidiendo (null si no hay petición en curso). */
  readonly loadingId = signal<string | null>(null);
  /**
   * idCompany cuyo detalle NO se pudo cargar por un error pasajero (red, petición cortada, timeout, 5xx) aun después
   * del reintento automático. El BusinessShell y el Diseñador muestran un estado de error con «Reintentar» (no es 404).
   */
  readonly loadError = signal<string | null>(null);
  /** Negocio que se quiere tener en `current`: evita que una respuesta tardía de otro negocio lo sobrescriba. */
  private wantedId: string | null = null;
  /** Petición en curso de GET /companies/{idCompany}, compartida para no pedirla dos veces. */
  private pending: { idCompany: string; req: Promise<ServerResponse<{ company: ICompany }> | null> } | null = null;
  /** Lista para el selector de negocio del sidebar. */
  readonly mine = signal<ICompanyListItem[]>([]);
  /** Contadores del sidebar. */
  readonly cardsCount = signal<number | null>(null);
  readonly sealsCount = signal<number | null>(null);

  /** El negocio abierto está inactivo (status 0). */
  readonly inactive = computed(() => this.current()?.status === 0);
  /**
   * Solo lectura: negocio abierto inactivo y el usuario NO es sudo. Todas las páginas del negocio lo usan para
   * deshabilitar (sin ocultar) sus acciones de escritura con el title «Negocio inactivo». El sudo nunca queda bloqueado.
   */
  readonly readOnly = computed(() => !this.auth.isSudo() && this.inactive());

  /* -------------------------------------------------------------------------- */
  /*                                 ANCHOR list                                 */
  /* -------------------------------------------------------------------------- */
  // region list
  list(query: ICompanyQuery = {}): Promise<ServerResponse<ICompanyList>> {
    return this.api.get<ICompanyList>('/companies', { ...query });
  }

  // region listSudo
  listSudo(query: ICompanyQuery = {}): Promise<ServerResponse<ICompanyList>> {
    return this.api.get<ICompanyList>('/sudo/companies', { ...query });
  }

  // region loadMine
  async loadMine(): Promise<void> {
    const res = await this.list({ orderBy: 'name', limit: 50 });
    if (res.ok) this.mine.set(this.forWeb(res.data?.companies ?? []));
  }

  // region forWeb
  /** Negocios que se administran en la web: el sudo ve todos; el resto, solo donde es Dueño o Administrador. */
  forWeb<T extends { companyRole?: string | null }>(list: T[]): T[] {
    return this.auth.isSudo() ? list : list.filter(c => !this.staffOnly(c));
  }

  // region staffOnly
  /** El usuario (no sudo) tiene en ese negocio un rol que no usa la web (Staff): solo la app CradleRewards Console. */
  staffOnly(c: { companyRole?: string | null }): boolean {
    return !this.auth.isSudo() && !!c.companyRole && !canUseWeb(c.companyRole);
  }

  /* -------------------------------------------------------------------------- */
  /*                                  ANCHOR get                                 */
  /* -------------------------------------------------------------------------- */
  // region get
  async get(idCompany: string): Promise<ServerResponse<{ company: ICompany }>> {
    const res = await this.api.get<{ company: ICompany }>(`/companies/${idCompany}`);
    if (res.ok && res.data?.company) {
      res.data.company.schedule ??= [];
      res.data.company.socialNetworks ??= [];
    }
    return res;
  }

  // region open
  /**
   * Pide el detalle y lo deja en `current`. Si el usuario (no sudo) es Staff en ese negocio, NO lo abre: avisa y
   * regresa a Negocios (el Staff solo usa la app CradleRewards Console). Mientras tanto la pantalla sigue en skeleton.
   */
  async open(idCompany: string): Promise<ServerResponse<{ company: ICompany }>> {
    this.wantedId ??= idCompany;
    const res = await this.get(idCompany);
    if (res.ok && res.data && this.wantedId === idCompany) {
      if (this.staffOnly(res.data.company)) {
        if (this.current()?.idCompany === idCompany) this.current.set(null);
        this.toast.info(STAFF_WEB_DENIED);
        this.router.navigate(['/dashboard']);
      } else {
        this.current.set(res.data.company);
      }
    }
    return res;
  }

  // region seed
  /**
   * Prepara la apertura de un negocio desde una lista (Negocios o selector del sidebar): el encabezado se pinta con
   * el item y `current` se vacía para que el detalle se pida fresco al entrar, sin bloquear la navegación.
   */
  seed(item: ICompanyListItem): void {
    this.preview.set(item);
    this.wantedId = item.idCompany;
    this.current.set(null);
    this.cardsCount.set(null);
    this.sealsCount.set(null);
  }

  // region ensure
  /**
   * Garantiza que `current` tenga el detalle completo de `idCompany`. No repite la petición si ya está cargado o si
   * ya hay una en curso para el mismo negocio (la comparte). Resuelve `null` cuando no hizo falta pedir nada.
   * La petición compartida se suelta al terminar (con éxito o con error): una respuesta fallida nunca queda guardada,
   * así que la siguiente llamada (reintento) hace una petición nueva.
   */
  ensure(idCompany: string): Promise<ServerResponse<{ company: ICompany }> | null> {
    this.wantedId = idCompany;
    if (this.current()?.idCompany === idCompany) return Promise.resolve(null);
    // Otro negocio: las páginas no deben ver el detalle anterior. (Desde el guard esto ocurre después de canDeactivate.)
    if (this.current()) this.current.set(null);
    if (this.pending?.idCompany === idCompany) return this.pending.req;
    this.loadingId.set(idCompany);
    const req: Promise<ServerResponse<{ company: ICompany }> | null> = this.open(idCompany).finally(() => {
      if (this.pending?.req !== req) return;
      this.pending = null;
      this.loadingId.set(null);
    });
    this.pending = { idCompany, req };
    return req;
  }

  // region enter
  /**
   * Entrada a una ruta del negocio (BusinessShell o Diseñador): espera el detalle (sin duplicar la petición del guard).
   * - 404 → «No encontramos ese negocio» y regresa a Negocios.
   * - 403 → «No tienes acceso a ese negocio» y regresa a Negocios.
   * - Cualquier otro error (0 = red caída o petición cortada, timeout, 5xx…): reintenta 1 vez tras ~1 s; si vuelve a
   *   fallar NO saca al usuario: marca `loadError` para que la pantalla muestre el estado de error con «Reintentar»
   *   (que vuelve a llamar a `enter`).
   * Resuelve true cuando `current` ya tiene ese negocio.
   */
  async enter(idCompany: string): Promise<boolean> {
    if (this.loadError() === idCompany) this.loadError.set(null);
    let res = await this.ensure(idCompany);
    if (res && !res.ok && !ApiService.isDefinitive(res.statusCode) && this.wantedId === idCompany) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (this.wantedId !== idCompany) return false; // ya se eligió otro negocio
      res = await this.ensure(idCompany); // petición nueva: la fallida ya se soltó
    }
    if (!res || res.ok) return this.current()?.idCompany === idCompany;
    if (this.wantedId !== idCompany) return false; // ya se eligió otro negocio
    if (ApiService.isDefinitive(res.statusCode)) {
      this.toast.error(res.statusCode === 403 ? 'No tienes acceso a ese negocio' : 'No encontramos ese negocio');
      this.router.navigate(['/dashboard']);
    } else {
      this.loadError.set(idCompany);
    }
    return false;
  }

  // region clear
  /** Cierra el negocio abierto (cerrar sesión, negocio eliminado). */
  clear(): void {
    this.wantedId = null;
    this.pending = null;
    this.loadingId.set(null);
    this.loadError.set(null);
    this.current.set(null);
    this.preview.set(null);
  }

  // region refresh
  async refresh(): Promise<void> {
    const id = this.current()?.idCompany;
    if (id) await this.open(id);
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR create                                */
  /* -------------------------------------------------------------------------- */
  // region create
  create(body: ICompanyCreateRequest): Promise<ServerResponse<{ company: ICompany }>> {
    return this.api.post<{ company: ICompany }>('/companies', body);
  }

  // region updateIdentity
  async updateIdentity(idCompany: string, body: ICompanyIdentityRequest): Promise<ServerResponse<{ company: ICompany }>> {
    const res = await this.api.put<{ company: ICompany }>(`/companies/${idCompany}`, body);
    if (res.ok) await this.refreshIfCurrent(idCompany);
    return res;
  }

  // region setStatus
  /**
   * PUT /companies/{idCompany}/status (solo sudo): 1 activa · 0 desactiva. Al guardar, refleja el nuevo estado en el
   * negocio abierto, en la vista previa y en «mis negocios» (selector del sidebar).
   */
  async setStatus(idCompany: string, status: CompanyStatus): Promise<ServerResponse<{ company: ICompany }>> {
    const res = await this.api.put<{ company: ICompany }>(`/companies/${idCompany}/status`, { status });
    if (res.ok) {
      const next = res.data?.company?.status ?? status;
      this.current.update(c => (c?.idCompany === idCompany ? { ...c, status: next } : c));
      this.preview.update(c => (c?.idCompany === idCompany ? { ...c, status: next } : c));
      this.mine.update(list => list.map(c => (c.idCompany === idCompany ? { ...c, status: next } : c)));
    }
    return res;
  }

  // region delete
  delete(idCompany: string, confirmName: string): Promise<ServerResponse<null>> {
    return this.api.delete<null>(`/companies/${idCompany}`, { confirmName });
  }

  // region putLocation
  async putLocation(idCompany: string, body: ICompanyAddress): Promise<ServerResponse<{ company: ICompany }>> {
    const res = await this.api.put<{ company: ICompany }>(`/companies/${idCompany}/location`, body);
    if (res.ok) await this.refreshIfCurrent(idCompany);
    return res;
  }

  // region deleteLocation
  async deleteLocation(idCompany: string): Promise<ServerResponse<null>> {
    const res = await this.api.delete<null>(`/companies/${idCompany}/location`);
    if (res.ok) await this.refreshIfCurrent(idCompany);
    return res;
  }

  // region putSchedule
  async putSchedule(idCompany: string, schedule: ISchedule[]): Promise<ServerResponse<{ schedule: ISchedule[] }>> {
    const res = await this.api.put<{ schedule: ISchedule[] }>(`/companies/${idCompany}/schedule`, { schedule });
    if (res.ok) await this.refreshIfCurrent(idCompany);
    return res;
  }

  // region putSocialNetworks
  async putSocialNetworks(idCompany: string, socialNetworks: ICompanySocialNetworkRequest[]): Promise<ServerResponse<{ socialNetworks: ICompanySocialNetwork[] }>> {
    const res = await this.api.put<{ socialNetworks: ICompanySocialNetwork[] }>(`/companies/${idCompany}/social-networks`, { socialNetworks });
    if (res.ok) await this.refreshIfCurrent(idCompany);
    return res;
  }

  private async refreshIfCurrent(idCompany: string): Promise<void> {
    if (this.current()?.idCompany === idCompany) await this.open(idCompany);
  }
}
